import React, { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Check, CreditCard, Wallet, Building2, Sparkles, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { scrollToSection } from "../lib/scrollToSection";
import { trackViewPrice } from "../lib/tracking";
import { useCheckoutMode } from "../lib/settings";

// Lista de beneficii — perechi par/impar afișate în 2 coloane pe sm+ (vezi
// grid-ul de mai jos). Ordinea contează: index par → stânga, impar → dreapta.
const FEATURES = [
  "10 module practice cu sesiuni live și activități aplicate",
  "Întâlniri live online cu exemple și aplicații practice",
  "Exemple și prompturi adaptate pentru activitatea la clasă",
  "Acces într-o comunitate activă de profesori",
  "Format flexibil, gândit pentru profesori activi",
  "Certificat de participare la finalizarea cursului",
  "Plata disponibilă inclusiv prin «Prima de carieră didactică»",
  "Program acreditat prin OMEC nr. 4712/19.08.2026",
  "1 credit ECTS la absolvire",
];

function formatRon(n) {
  return Number(n).toLocaleString("ro-RO");
}

const scrollToId = (id) => scrollToSection(id);

export default function Pricing() {
  const [price, setPrice] = useState(null); // effective (discount) price — what user actually pays
  const [standardPrice, setStandardPrice] = useState(null); // marketing anchor, optional
  const [discountBadge, setDiscountBadge] = useState("");
  const [currency, setCurrency] = useState("RON");
  const navigate = useNavigate();
  const { isOnline, programStatus } = useCheckoutMode();

  useEffect(() => {
    api.get("/settings/price").then((r) => {
      setPrice(r.data.price);
      setCurrency(r.data.currency || "RON");
      setStandardPrice(r.data.standard_price ?? null);
      setDiscountBadge((r.data.discount_badge || "").trim());
      trackViewPrice(r.data.price, r.data.currency);
    }).catch(() => {});
  }, []);

  const onEnroll = () => {
    // PR-T1 (G5): begin_checkout fires only on Checkout mount to avoid
    // double-counting when the user clicks here AND lands on /checkout.
    navigate("/checkout");
  };

  const hasDiscount = standardPrice !== null && price !== null && Number(standardPrice) > Number(price);
  const savings = hasDiscount ? Number(standardPrice) - Number(price) : 0;
  const badgeLabel = (discountBadge || (hasDiscount ? "Ofertă de lansare" : "")).trim();

  return (
    <section id="pricing" className="scroll-mt-28 py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto">
          <span className="inline-flex items-center rounded-full bg-[#F0E7F4] px-3 py-1 text-xs font-semibold text-[#965EA7] uppercase tracking-widest">
            Investiție în cariera ta
          </span>
          <h2 className="mt-4 font-display text-3xl md:text-5xl font-bold text-[#373A45] !leading-[1.15] tracking-tight">
            Alege metoda de înscriere potrivită pentru tine.
          </h2>
          <p className="mt-5 text-base md:text-lg text-[#6B7280] leading-relaxed">
            Poți participa prin <strong className="text-[#373A45]">cardul Prima de carieră didactică</strong>,
            din <strong className="text-[#373A45]">fonduri proprii</strong> sau prin
            <strong className="text-[#373A45]"> instituția de învățământ</strong>.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 lg:items-stretch">
          {/* Left: main price card */}
          <div className="lg:col-span-7 flex">
            <div
              className="bg-white rounded-3xl border-2 border-[#965EA7] p-8 md:p-10 shadow-xl relative flex-1 flex flex-col"
              data-testid="pricing-card"
            >
              {/* Top badges row */}
              <div className="absolute -top-3 left-8 flex items-center gap-2">
                <div className="bg-[#965EA7] text-white text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-widest">
                  Înscriere deschisă
                </div>
                {hasDiscount && badgeLabel && (
                  <div
                    className="inline-flex items-center gap-1.5 bg-white text-[#965EA7] border-2 border-[#965EA7] text-xs font-bold px-3 py-1 rounded-full uppercase tracking-widest shadow-sm"
                    data-testid="pricing-discount-badge"
                  >
                    <Sparkles size={12} /> {badgeLabel}
                  </div>
                )}
              </div>

              {/* Standard (anchor) price — small, muted, strikethrough */}
              {hasDiscount && (
                <div className="mt-2 flex items-center gap-2" data-testid="pricing-standard-row">
                  <span className="text-sm font-medium text-[#6B7280]">
                    Preț standard:
                  </span>
                  <span
                    className="text-base text-[#9CA3AF] line-through font-medium"
                    data-testid="pricing-standard-price"
                  >
                    {formatRon(standardPrice)} {currency}
                  </span>
                </div>
              )}

              {/* Effective price — large, dominant */}
              <div className="mt-1 flex items-end gap-3">
                <div
                  className="font-display text-6xl md:text-7xl font-bold text-[#373A45] leading-none tracking-tight"
                  data-testid="pricing-price"
                >
                  {price !== null ? formatRon(price) : "—"}
                </div>
                <div className="text-[#6B7280] mb-2 text-lg font-medium">{currency}</div>
              </div>

              <div className="mt-2 text-sm text-[#6B7280]">
                TVA inclus · Acces complet la curs · Fără taxe suplimentare
              </div>

              {hasDiscount && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#F0E7F4] text-[#965EA7] px-3 py-1 text-xs font-semibold"
                    data-testid="pricing-savings"
                  >
                    Preț special de lansare · Economisești {formatRon(savings)} {currency}
                  </span>
                </div>
              )}

              <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#F0E7F4] text-[#965EA7] px-3 py-1 text-xs font-semibold">
                25 de ore de formare (15 live + 10 individual) · program acreditat
              </div>

              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {FEATURES.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-[#373A45] text-sm">
                    <Check size={18} className="text-[#519872] mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={onEnroll}
                  data-testid="pricing-enroll-button"
                  className="rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white font-semibold text-base px-8 py-6 shadow-[0_4px_14px_0_rgba(150,94,167,0.39)] hover:-translate-y-0.5 transition-all"
                >
                  {isOnline ? "Înscrie-te la curs" : "Înscrie-te"}
                </Button>
                {/* Hidden wherever its target is: scrolling to a
                    `display:none` element does nothing, so on a phone this
                    button would simply not respond. */}
                <Button
                  variant="outline"
                  onClick={() => scrollToId("enroll-methods")}
                  className="hidden md:inline-flex rounded-full border-2 border-[#373A45] text-[#373A45] hover:bg-[#373A45] hover:text-white font-semibold text-base px-8 py-6"
                  data-testid="pricing-methods-cta"
                >
                  Vezi metode de plată
                </Button>
              </div>

              {!isOnline && (
                <p
                  className="mt-3 text-xs text-[#6B7280] leading-relaxed"
                  data-testid="pricing-reassurance-note"
                >
                  Nu este necesară plata imediată. Echipa noastră te va contacta
                  pentru confirmare și stabilirea pașilor următori.
                </p>
              )}

              {programStatus === "paused" && (
                <div className="mt-6 inline-flex items-center gap-2 rounded-2xl bg-[#FDECEC] text-[#9b1f2c] px-4 py-3 text-sm" data-testid="pricing-paused-banner">
                  Înscrierile sunt temporar pe pauză. Revenim curând.
                </div>
              )}
            </div>
          </div>

          {/* Right: 3 enrollment methods, stacked. On desktop, stretch to match
              the left card's height so the columns align top + bottom. */}
          {/* Hidden on phones. Three full-width cards there add a long scroll
              between the price and the cohort schedule below it, and every
              claim they carry already reaches a phone elsewhere: the feature
              list in this card names both the accreditation and the Prima
              card, and "Prima de carieră" is its own section in the nav. From
              `md` up the column sits beside the price, where it costs no
              scrolling at all. */}
          <div
            id="enroll-methods"
            className="hidden md:flex lg:col-span-5 flex-col gap-4 scroll-mt-28"
            data-testid="enroll-methods"
            aria-labelledby="enroll-methods-title"
          >
            <h3 id="enroll-methods-title" className="sr-only">
              Metode de înscriere disponibile
            </h3>

            {/* Card 1 — Card Prima (purple accent) */}
            <article
              className="group bg-[#FAF6FC] rounded-2xl border border-[#E9DCEF] p-6 transition-all hover:border-[#965EA7]/60 hover:bg-white hover:shadow-md lg:flex-1 flex"
              data-testid="enroll-method-prima"
            >
              <div className="flex items-start gap-4">
                <div className="h-11 w-11 rounded-xl bg-white border border-[#E9DCEF] text-[#965EA7] grid place-items-center shrink-0 transition-colors group-hover:bg-[#F0E7F4]">
                  <CreditCard size={20} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h4 className="font-display text-lg font-semibold text-[#373A45]">
                      Card Prima de carieră didactică
                    </h4>
                  </div>
                  <p className="mt-2 text-sm text-[#6B7280] leading-relaxed">
                    Cursul este acreditat prin OMEC nr. 4712/19.08.2026 — condiția
                    necesară pentru decontarea prin cardul Prima de carieră didactică.
                  </p>
                  <p className="mt-3 text-xs text-[#965EA7] font-semibold">
                    Te poți înscrie încă de acum.
                  </p>
                </div>
              </div>
            </article>

            {/* Card 2 — Plată directă (green accent) */}
            <article
              className="group bg-[#F4FAF6] rounded-2xl border border-[#D8EBDE] p-6 transition-all hover:border-[#519872]/60 hover:bg-white hover:shadow-md lg:flex-1 flex"
              data-testid="enroll-method-direct"
            >
              <div className="flex items-start gap-4">
                <div className="h-11 w-11 rounded-xl bg-white border border-[#D8EBDE] text-[#519872] grid place-items-center shrink-0 transition-colors group-hover:bg-[#EAF4EE]">
                  <Wallet size={20} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h4 className="font-display text-lg font-semibold text-[#373A45]">
                      Plată directă
                    </h4>
                    <span className="inline-flex items-center gap-1 rounded-full bg-white text-[#519872] border border-[#D8EBDE] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider">
                      Disponibil acum
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-[#6B7280] leading-relaxed">
                    Poți achita cursul oricând din fonduri proprii și îți poți
                    rezerva imediat locul într-una dintre grupele disponibile.
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#519872] font-semibold">
                    <span className="inline-flex items-center gap-1">
                      <ShieldCheck size={13} /> Plată online securizată
                    </span>
                  </div>
                </div>
              </div>
            </article>

            {/* Card 3 — Instituție (blue accent) */}
            <article
              className="group bg-[#F2F8FC] rounded-2xl border border-[#D6E5F1] p-6 transition-all hover:border-[#3F8EBA]/60 hover:bg-white hover:shadow-md lg:flex-1 flex"
              data-testid="enroll-method-institution"
            >
              <div className="flex items-start gap-4">
                <div className="h-11 w-11 rounded-xl bg-white border border-[#D6E5F1] text-[#3F8EBA] grid place-items-center shrink-0 transition-colors group-hover:bg-[#E4EFF6]">
                  <Building2 size={20} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h4 className="font-display text-lg font-semibold text-[#373A45]">
                      Instituție de învățământ / SEAP
                    </h4>
                    <span className="inline-flex items-center gap-1 rounded-full bg-white text-[#3F8EBA] border border-[#D6E5F1] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider">
                      Pentru instituții
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-[#6B7280] leading-relaxed">
                    Școlile și instituțiile publice pot achiziționa cursul prin
                    SEAP/SICAP.
                  </p>
                  <button
                    type="button"
                    onClick={() => scrollToId("lead-form")}
                    className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#3F8EBA] hover:text-[#2E6F94] underline-offset-4 hover:underline transition-colors"
                    data-testid="enroll-method-institution-cta"
                  >
                    Contactează-ne pentru ofertă →
                  </button>
                </div>
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>
  );
}
